import { del, get, post, put, type ReqConfig } from '../request';
import { toPage, type Page } from './types';

/** 流程模板 */
export interface WorkflowTemplate {
  id: number;
  code: string;
  name: string;
  /** v1.12 流程用途描述 */
  description?: string;
  /** v1.13 流程分组（流程设计页按分类分模块查看） */
  category?: string;
  entity_type: string;
  enabled: boolean;
  created_at?: string;
}

/** 流程节点 */
export interface WorkflowNode {
  id?: number;
  template_id?: number;
  key: string;
  name: string;
  node_type: 'start' | 'approve' | 'task' | 'notify' | 'condition' | 'end';
  approver_type?: 'role' | 'user' | 'creator_leader' | 'skill_group';
  approver_value?: string;
  role_filter?: string;
  sign_type?: 'or' | 'all';
  reject_to_key?: string;
  sla_hours?: number;
  comment_req?: boolean;
  /** 本环节处理人可指定下一环节处理人（approve/task） */
  specify_next?: boolean;
  condition_json?: string;
  /** 核心节点标注（v1.14：坐席辅助/流程可视化突出关键节点） */
  is_core?: boolean;
  x?: number;
  y?: number;
  sort?: number;
}

/** 流程边 */
export interface WorkflowEdge {
  id?: number;
  from_key: string;
  to_key: string;
  branch?: '' | 'true' | 'false';
}

export interface WorkflowBinding {
  entity_type: string;
  template_id: number;
  enabled: boolean;
}

export interface WorkflowTask {
  id: number;
  instance_id: number;
  node_id: number;
  node_name: string;
  node_type: string;
  assignee_id?: number | null;
  state: string;
  comment?: string;
  sla_due_at?: string;
  /** 本环节处理人可指定下一环节处理人（详情按快照解析） */
  specify_next?: boolean;
  /** 当前环节审批意见是否必填（comment_req，详情按快照解析；T-01 联动前端前置校验） */
  comment_req?: boolean;
  /** 当前节点配置的技能组（approver_type=skill_group 时注入，候选下拉按组过滤） */
  skill_group_id?: number | null;
  /** 转派时间（已转派 tab 过滤依据） */
  transferred_at?: string | null;
  /** 升级时间（已升级 tab 过滤依据） */
  escalated_at?: string | null;
  /** 转派/升级操作者（已转派/已升级 tab 双视角过滤依据） */
  transferred_by?: number | null;
  escalated_by?: number | null;
  created_at?: string;
}

/** 转派/指定下一环节处理人的候选用户（GET /workflow/task-candidates，全角色） */
export interface TaskCandidate {
  id: number;
  name: string;
  username: string;
  skill_group_id?: number | null;
}

export interface WorkflowInstance {
  id: number;
  template_id: number;
  entity_type: string;
  entity_id: string;
  current_node_id: number;
  state: string;
  /** 审批人指定的下一环节处理人（会签 all 先批者指定持久化，推进后清空） */
  next_assignee_id?: number | null;
  created_by: number;
  started_at: string;
  finished_at?: string;
  tasks?: WorkflowTask[];
}

export interface WorkflowActionLog {
  id: number;
  instance_id: number;
  actor_id: number;
  action: string;
  detail: string;
  created_at?: string;
}

export function listWorkflowTemplates(): Promise<WorkflowTemplate[]> {
  return get('/workflow-templates');
}

/** 发起工单时选择审核流程（全角色；仅返回启用中的模板） */
export async function listSelectableTemplates(entityType: string): Promise<WorkflowTemplate[]> {
  const list = await get('/workflow/templates', { entity_type: entityType });
  return Array.isArray(list) ? list : [];
}
export function createWorkflowTemplate(t: { code: string; name: string; entity_type: string }): Promise<WorkflowTemplate> {
  return post('/workflow-templates', t);
}
export function updateWorkflowTemplate(id: number, t: Partial<WorkflowTemplate>) {
  return put(`/workflow-templates/${id}`, t);
}
export function deleteWorkflowTemplate(id: number) {
  return del(`/workflow-templates/${id}`);
}
export async function getWorkflowGraph(id: number): Promise<{ nodes: WorkflowNode[]; edges: WorkflowEdge[] }> {
  return get(`/workflow-templates/${id}/graph`);
}
export function saveWorkflowGraph(id: number, nodes: WorkflowNode[], edges: WorkflowEdge[]) {
  return put(`/workflow-templates/${id}/graph`, { nodes, edges });
}
export function setWorkflowBinding(templateId: number, entityType: string, enabled: boolean) {
  return put(`/workflow-templates/${templateId}/binding`, { entity_type: entityType, enabled });
}
export function listWorkflowBindings(): Promise<WorkflowBinding[]> {
  return get('/workflow/bindings');
}

export function startWorkflowInstance(entityType: string, entityId: string): Promise<WorkflowInstance> {
  return post('/workflow-instances', { entity_type: entityType, entity_id: entityId });
}
export async function getWorkflowByEntity(entityType: string, entityId: string): Promise<{ found: boolean; instance?: WorkflowInstance }> {
  return get('/workflow/instances', { entity_type: entityType, entity_id: entityId });
}
export function getWorkflowInstance(id: number): Promise<WorkflowInstance> {
  return get(`/workflow/instances/${id}`);
}
export function cancelWorkflowInstance(id: number) {
  return post(`/workflow/instances/${id}/cancel`, {});
}
export function getWorkflowTimeline(id: number): Promise<WorkflowActionLog[]> {
  return get(`/workflow/instances/${id}/timeline`);
}

/** 我的待办任务（待办中心轮询 + 顶栏铃铛刷新，默认静默：失败不弹全局 toast）；
 *  需要失败提示的调用方传 { silent: false }。 */
export async function listMyWorkflowTasks(q?: {
  status?: string;
  /** 对象类型过滤：ticket，空=全部 */
  entityType?: string;
  /** 工单号（数字精确）或 标题/节点名（文本模糊）检索 */
  keyword?: string;
  page?: number;
  pageSize?: number;
}, cfg?: ReqConfig): Promise<Page<WorkflowTask>> {
  return toPage<WorkflowTask>(
    await get('/workflow/my-tasks', {
      page: q?.page ?? 1,
      pageSize: q?.pageSize ?? 10,
      status: q?.status || undefined,
      entity_type: q?.entityType || undefined,
      keyword: q?.keyword || undefined,
    }, { silent: true, ...(cfg || {}) })
  );
}

/** 候选处理人（转派/指定下一环节处理人；skillGroupId 为空=全部启用用户，全角色可读） */
export async function listTaskCandidates(skillGroupId?: number): Promise<TaskCandidate[]> {
  const list = await get('/workflow/task-candidates', skillGroupId ? { skill_group_id: skillGroupId } : undefined);
  return Array.isArray(list) ? list : [];
}

export function approveWorkflowTask(id: number, comment?: string, nextAssigneeId?: number) {
  return post(`/workflow-tasks/${id}/approve`, {
    comment: comment || '',
    ...(nextAssigneeId ? { next_assignee_id: nextAssigneeId } : {}),
  });
}
export function rejectWorkflowTask(id: number, comment: string) {
  return post(`/workflow-tasks/${id}/reject`, { comment });
}
export function transferWorkflowTask(id: number, userId: number) {
  return post(`/workflow-tasks/${id}/transfer`, { user_id: userId });
}
export function escalateWorkflowTask(id: number) {
  return post(`/workflow-tasks/${id}/escalate`, {});
}
export function completeWorkflowTask(id: number, comment?: string, nextAssigneeId?: number) {
  return post(`/workflow-tasks/${id}/complete`, {
    comment: comment || '',
    ...(nextAssigneeId ? { next_assignee_id: nextAssigneeId } : {}),
  });
}
